import * as React from 'react';

import { Group, Panel, Separator } from 'react-resizable-panels';

import { ChatMobileSheet } from '@modules/_shared/components/chat/ChatMobileSheet';
import { useChatPanel } from '@modules/_shared/components/chat/ChatPanelContext';
import { MOBILE_CHAT_BREAKPOINT } from '@modules/_shared/components/chat/chatPanelDefaults';
import { ChatPanelInner } from '@modules/_shared/components/chat/ChatPanelInner';
import { DrawerContainerProvider } from '@modules/_shared/hooks/drawerContainerProvider';
import { useWindowInnerSize } from '@shared/hooks/useWindowInnerSize';
import { useTheme } from '@shared/theme/theme';

const mainPanelCss = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  overflow: 'clip',
  position: 'relative',
} as const;

export const ChatPanelLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { open, toggle } = useChatPanel();
  const { colors, muiDrawerZIndex } = useTheme();
  const [windowWidth] = useWindowInnerSize();
  const mainPanelRef = React.useRef<HTMLDivElement>(null);
  const drawerContainer = React.useMemo(
    () => ({
      get current() {
        return mainPanelRef.current;
      },
      contained: open,
    }),
    [open],
  );
  const isMobile = windowWidth <= MOBILE_CHAT_BREAKPOINT;

  if (isMobile) {
    return (
      <div css={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
        <div
          ref={mainPanelRef}
          // Read by the agent's read_current_page tool (currentPage.ts).
          data-chat-page=""
          css={{
            ...mainPanelCss,
            flex: 1,
            ...(open && { contain: 'layout paint' }),
            // without this, wide content (data grids, nowrap rows) inflates
            // this row-flex item past the viewport and gets clipped
            minWidth: 0,
          }}
        >
          <DrawerContainerProvider value={drawerContainer}>
            {children}
            {open && (
              <ChatMobileSheet containerRef={mainPanelRef} onClose={toggle}>
                <ChatPanelInner onClose={toggle} isMobile={true} />
              </ChatMobileSheet>
            )}
          </DrawerContainerProvider>
        </div>
      </div>
    );
  }

  return (
    <Group
      orientation="horizontal"
      resizeTargetMinimumSize={{ fine: 12, coarse: 24 }}
      css={{ flex: 1, minWidth: 0, minHeight: 0 }}
    >
      <Panel id="main" minSize="30%" defaultSize="75%">
        <div
          ref={mainPanelRef}
          // Read by the agent's read_current_page tool (currentPage.ts).
          data-chat-page=""
          css={{ ...mainPanelCss, ...(open && { contain: 'layout paint' }) }}
        >
          <DrawerContainerProvider value={drawerContainer}>
            {children}
          </DrawerContainerProvider>
        </div>
      </Panel>
      {open && (
        <>
          <Separator
            style={{
              width: 0,
              cursor: 'col-resize',
              position: 'relative',
              zIndex: muiDrawerZIndex + 1,
            }}
            css={{
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: -0.5,
                width: 1,
                backgroundColor: colors.divider,
                transition: 'background-color 0.15s, width 0.15s, left 0.15s',
                pointerEvents: 'none',
              },
              '&:hover::before, &[data-resize-handle-state="hover"]::before': {
                backgroundColor: colors.blue100,
                width: 2,
                left: -1,
              },
              '&[data-resize-handle-active]::before, &[data-resize-handle-state="drag"]::before':
                {
                  backgroundColor: colors.primaryMain,
                  width: 2,
                  left: -1,
                },
            }}
          />
          <Panel id="chat" minSize="15%" maxSize="50%" defaultSize="25%">
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                position: 'relative',
                zIndex: muiDrawerZIndex + 1,
              }}
            >
              <ChatPanelInner
                onClose={toggle}
                tableContainerRef={mainPanelRef}
              />
            </div>
          </Panel>
        </>
      )}
    </Group>
  );
};
